import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReorderRuleExportConfigurationContext } from '../../../../../../../context/ReorderRuleExportConfigurationContext';
import { Routing } from '../../../../../../../util/routes.util';
import { BackButton, ButtonVariant, DeleteButton } from '../../../../../../components/Button';
import UpdateButton from '../../../../../../components/Button/templates/UpdateButton';
import DeleteReorderRuleExportConfigurationModal from './Modals/DeleteReorderRuleExportConfigurationModal';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import { useRequest } from '../../../../../../../requests/common/requestHook';
import { ReorderRuleExportConfigurationRequest } from '../../../../../../../requests/reorderRuleExportConfiguration.request';
import ReorderRuleExportConfigurationForm, {
  DraggableReorderRuleExportFieldIdentifier,
} from '../Common/ReorderRuleExportConfigurationForm';
import {
  ReorderRuleExportField,
  ReorderRuleExportConfigurationInput,
  ReorderRuleExportConfiguration,
} from '../../../../../../../types/reorder_rule_export_configuration';
import { CompanyContext } from '../../../../../../../context/CompanyContext';

interface UpdateReorderRuleExportConfigurationPaneProps {}

export default function UpdateReorderRuleExportConfigurationPane({}: UpdateReorderRuleExportConfigurationPaneProps) {
  const id = useParams()['id'] || '';
  if (!id) return null;

  const navigate = useNavigate();
  const { currentCompany } = useContext(CompanyContext);

  const {
    reorderRuleExportConfigurations,
    setReorderRuleExportConfigurations,
    reorderRuleExportConfigurationsLoading,
  } = useContext(ReorderRuleExportConfigurationContext);

  const existing = reorderRuleExportConfigurations.get(id);
  const [input, setInput] = useState<ReorderRuleExportConfigurationInput>(
    new ReorderRuleExportConfigurationInput(existing || ReorderRuleExportConfiguration.default(currentCompany.id)),
  );

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [update, { loading: updateLoading }] = useRequest(ReorderRuleExportConfigurationRequest.update);

  const handleUpdate = async (
    configuration: ReorderRuleExportConfigurationInput,
    items: DraggableReorderRuleExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<ReorderRuleExportField>(items);

    const result = await update({ data: [configuration.forUpdate()] });
    if (!result) return;

    const config = result[0];
    reorderRuleExportConfigurations.set(config.id, new ReorderRuleExportConfiguration(config));
    setReorderRuleExportConfigurations(new Map(reorderRuleExportConfigurations));
    navigate(Routing.productMasterData.settings.stockTriggers.settings.report.overview());
  };

  useEffect(() => {
    if (!reorderRuleExportConfigurationsLoading && existing) {
      setInput(new ReorderRuleExportConfigurationInput(existing));
    }
  }, [reorderRuleExportConfigurationsLoading]);

  const footer = (
    configuration: ReorderRuleExportConfigurationInput,
    items: DraggableReorderRuleExportFieldIdentifier[],
  ) => {
    return (
      <div className='flex justify-between'>
        <div>
          <DeleteButton
            disabled={reorderRuleExportConfigurationsLoading || updateLoading}
            onClick={() => setOpenDeleteModal(true)}
          />
        </div>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={updateLoading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.productMasterData.settings.stockTriggers.settings.report.overview())}
          />
          <UpdateButton
            loading={updateLoading}
            disabled={reorderRuleExportConfigurationsLoading || !configuration.filename || !configuration.name}
            onClick={() => handleUpdate(configuration, items)}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <DeleteReorderRuleExportConfigurationModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        ids={new Set([id])}
      />
      <ReorderRuleExportConfigurationForm
        loading={reorderRuleExportConfigurationsLoading}
        input={input}
        setInput={setInput}
        footer={footer}
      />
    </>
  );
}
